import React, { useEffect, useState } from "react";
import { X, TrendingUp, Download } from "lucide-react";
import { toast } from "sonner";
import { api, extractErrorMessage } from "@/lib/api";

const tl = (v) => `${Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;

export function SalaryDiffModal({ yil: y0, onClose }) {
  const [yil, setYil] = useState(y0);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    api.get("/payroll/salary-diff", { params: { yil } }).then((r) => setD(r.data)).catch((e) => setErr(extractErrorMessage(e, "Rapor yüklenemedi")));
  }, [yil]);
  const excel = async () => {
    try {
      const r = await api.get("/payroll/salary-diff.xlsx", { params: { yil }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `maas_fark_raporu_${yil}.xlsx`; a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("Excel indirilemedi"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[88vh] flex flex-col" data-testid="salary-diff-modal">
        <div className="p-4 border-b border-slate-200 flex items-center gap-2">
          <TrendingUp size={16} />
          <h3 className="font-bold text-slate-900 flex-1">Maaş Fark Raporu</h3>
          <select value={yil} onChange={(e) => setYil(Number(e.target.value))} data-testid="salary-diff-year" className="border border-slate-200 rounded px-2 py-1 text-sm">
            {[y0 - 2, y0 - 1, y0].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={excel} disabled={!d?.rows?.length} data-testid="salary-diff-excel" className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg"><Download size={13} /> Excel</button>
          <button onClick={onClose} data-testid="salary-diff-close" className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
        </div>
        <p className="px-4 py-2 text-[11px] text-slate-500 border-b border-slate-100">Kilitlenmiş (ödenmiş) aylarda saklanan bordro ile bugünkü maaş geçmişine göre yeniden hesaplanan bordro karşılaştırılır. Yalnızca maaşı geriye dönük değişen personel listelenir.</p>
        {err && <div className="p-4 text-sm text-rose-700">{err}</div>}
        {!d && !err && <div className="p-6 text-sm text-slate-500">Hesaplanıyor…</div>}
        {d && (d.rows.length === 0 ? (
          <div className="p-6 text-sm text-slate-500" data-testid="salary-diff-empty">{yil} yılında kilitli aylar için geriye dönük maaş farkı yok.</div>
        ) : (
          <div className="overflow-auto">
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 text-sm" data-testid="salary-diff-total">
              Toplam ödenmesi gereken net fark: <b className="font-mono-tab">{tl(d.toplam_fark)}</b> · {d.kisiler.length} personel
            </div>
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white sticky top-0">
                <tr><th className="px-3 py-2 text-left">Dönem</th><th className="px-3 py-2 text-left">Personel</th><th className="px-3 py-2 text-right">Eski Maaş</th><th className="px-3 py-2 text-right">Yeni Maaş</th><th className="px-3 py-2 text-right">Ödenen Net</th><th className="px-3 py-2 text-right">Olması Gereken</th><th className="px-3 py-2 text-right">Net Fark</th></tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={`${r.personnel_id}-${r.ay}`} className="border-t border-slate-100" data-testid={`salary-diff-row-${r.personnel_id}-${r.ay}`}>
                    <td className="px-3 py-2 text-slate-600">{r.donem}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900">{r.ad_soyad}</td>
                    <td className="px-3 py-2 text-right font-mono-tab">{tl(r.eski_maas)}</td>
                    <td className="px-3 py-2 text-right font-mono-tab">{tl(r.yeni_maas)}</td>
                    <td className="px-3 py-2 text-right font-mono-tab text-slate-600">{tl(r.odenen_net)}</td>
                    <td className="px-3 py-2 text-right font-mono-tab">{tl(r.olmasi_gereken_net)}</td>
                    <td className={`px-3 py-2 text-right font-mono-tab font-bold ${r.fark_net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{r.fark_net >= 0 ? "+" : ""}{tl(r.fark_net)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {d.kisiler.map((p) => (
                  <tr key={p.personnel_id} className="bg-slate-50 border-t border-slate-200 text-xs" data-testid={`salary-diff-person-${p.personnel_id}`}>
                    <td className="px-3 py-1.5 text-slate-500">Toplam · {p.ay_sayisi} ay</td>
                    <td className="px-3 py-1.5 font-semibold" colSpan={5}>{p.ad_soyad}</td>
                    <td className="px-3 py-1.5 text-right font-mono-tab font-bold">{tl(p.toplam_fark)}</td>
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
